import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Paper, Button, Box, ImageList, ImageListItem, Card, CardMedia, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [userImages, setUserImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleViewAll = () => {
    if (isAuthenticated) {
      navigate('/image-generator');
    } else {
      navigate('/signin', { state: { from: '/image-generator' } });
    }
  };

  useEffect(() => {
    if (token) {
      fetchUserImages();
    }
  }, [token]);

  const fetchUserImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/user-images`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUserImages(response.data.slice(0, 6)); // Show only the 6 most recent images
    } catch (error) {
      console.error('Error fetching user images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateClick = () => {
    if (isAuthenticated) {
      navigate('/image-generator');
    } else {
      navigate('/signin', { state: { from: '/image-generator' } });
    }
  };

  // Example images data
  const exampleImages = [
    {
      url: 'https://replicate.delivery/pbxt/IJE0TKrdhqkuKUKwLPZYIGQYHjB5U5QLkLwKPCPtSgzrGRkRA/out-0.png',
      title: 'Cyberpunk City',
      prompt: 'Cyberpunk cityscape at night with neon lights and flying cars'
    },
    {
      url: 'https://replicate.delivery/pbxt/UjQXZgLVXXG5JBaJJqh6ALYBTbth3XPnUTJwzE0WzrgigPkRA/out-0.png',
      title: 'Fantasy World',
      prompt: 'Magical floating islands with waterfalls and crystal formations'
    },
    {
      url: 'https://replicate.delivery/pbxt/QzPf3pH3ggCwKMGUJbhyrbHKLQIltLKBZCwkW3Qh7bz6xPkRA/out-0.png',
      title: 'Space Explorer',
      prompt: 'Astronaut standing on an alien planet with two moons in the sky'
    },
    {
      url: 'https://replicate.delivery/pbxt/AdOm5Xt9JRDyxKEPCQpZoHlszELvxBPfNC7TIhWj9jYAnPkRA/out-0.png',
      title: 'Nature Dream',
      prompt: 'Enchanted forest with bioluminescent plants and mystical creatures'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" gutterBottom>
              Welcome to Tripod AI Image Generator
            </Typography>
            <Typography variant="h5" component="h2" gutterBottom color="textSecondary">
              This tool is designed to generate image based on your prompt. 
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              onClick={handleGenerateClick}
              sx={{ 
                mt: 2,
                color: '#000',
                fontWeight: 500
              }}
            >
              Generate Image
            </Button>
          </Paper>
        </Grid>

        {/* Trending Creations Section */}
        <Grid item xs={12}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3, 
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            Trending Creations
            <Button 
              variant="text" 
              onClick={handleViewAll}
              sx={{ 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(0, 255, 234, 0.08)' }
              }}
            >
              View All
            </Button>
          </Typography>
          
          {token ? (
            loading ? (
              <Grid container spacing={2}>
                {[...Array(6)].map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={200}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : userImages.length > 0 ? (
              <Grid container spacing={2}>
                {userImages.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.02)',
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={`${API_URL.replace('/api', '')}${image.url}`}
                        alt={image.prompt || 'Generated image'}
                        sx={{
                          height: 200,
                          objectFit: 'cover'
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No images generated yet. Try creating your first one!
                </Typography>
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Sign in to see your generated images
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export default Home; 